import { useState } from 'react';
import { Dropdown } from '@repo/atomic-components';
import type { DropdownChangeEvent } from '@repo/atomic-components';
import { useLazyPostSwitchOrgQuery } from '@/api';
import type { ListOrg } from '@/api/debug/listOrgs';
import { useOrganisations } from '@/hooks';
import { selectUserAboutData, useAppSelector } from '@/store';
import type { SettingControlProps } from '../types';

/** Organisation switcher for global admins. Switching reloads the app. */
export const OrganisationControl = ({ labelId }: SettingControlProps) => {
  const userAboutData = useAppSelector(selectUserAboutData);
  const orgs = useOrganisations();
  const [triggerPostSwitchOrg] = useLazyPostSwitchOrgQuery();
  const [pendingOrg, setPendingOrg] = useState<ListOrg | null>(null);

  // Until the user picks one, the selection is whichever org `/user/about` says they are in.
  const selectedOrg = pendingOrg ?? orgs.find((org) => org.orgId === userAboutData?.organisationId) ?? null;

  const handleChange = (event: DropdownChangeEvent) => {
    const org = event.value as ListOrg;
    setPendingOrg(org);
    triggerPostSwitchOrg({ orgId: org.orgId })
      .unwrap()
      .then(() => window.location.reload())
      .catch((error) => console.error('Failed to switch org:', error));
  };

  return (
    <Dropdown
      aria-labelledby={labelId}
      value={selectedOrg}
      options={orgs}
      optionLabel="name"
      onChange={handleChange}
      placeholder="Select organisation"
      className="!bg-theme-white w-full max-w-90"
      // Keyboard focus, not the pointer, drives the highlight — a hovered option otherwise
      // sticks and hijacks the scroll position the next time the panel opens.
      focusOnHover={false}
      panelClassName="[&_.p-dropdown-item]:!whitespace-normal [&_.p-dropdown-item]:!break-words"
      itemTemplate={(org: ListOrg) => <span className="block max-w-full min-w-0 break-words">{org.name}</span>}
      valueTemplate={(org: ListOrg | null) =>
        org ? (
          <span className="!text-theme-black">{org.name}</span>
        ) : (
          <span className="text-fletchers-gray-200">Select organisation</span>
        )
      }
    />
  );
};
